import { useEffect, useRef, useCallback, useState } from "react";
import axios from "axios";

/**
 * Shared popup-OAuth mechanics for every Direct Data Import integration.
 *
 * Most providers drive their connect flow through `useOAuthConnection`, but a
 * few (Intercom, Airtable) own bespoke connect logic. Both paths use the helpers
 * here so the popup behavior — open synchronously, show a loading screen, return
 * via postMessage, fall back to a full-page redirect only when blocked — lives in
 * exactly one place.
 */

// Minimal neutral "Connecting…" screen painted into the popup the instant it
// opens, so the user never stares at a stark about:blank window while the
// /connect POST is in flight (it can take a few seconds). Replaced by the
// provider's page once we set popup.location.
//
// Deliberately dependency-free, default-colored text: this lives in a separate
// browser window with none of the app's React tree or oolib styles, so an oolib
// component can't render here. The visible loading affordance (oolib's button
// `isLoading`) is shown in the parent app via the `isConnecting` flag below.
// `label` is a trusted in-code provider name.
const _popupLoadingHtml = (label) =>
  `<!doctype html><html><head><meta charset="utf-8"><title>Connecting…</title></head><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui,-apple-system,sans-serif"><p>Connecting to ${label || "the provider"}…</p></body></html>`;

/**
 * Open a centered popup synchronously. MUST be called inside the click stack —
 * opening it after an `await` gets blocked by popup blockers. Returns the window
 * handle, or null if the browser blocked it (caller falls back to a full-page
 * redirect).
 */
export const openOAuthPopup = (name, label) => {
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  const popup = window.open(
    "",
    name,
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
  );
  if (popup) {
    try {
      popup.document.write(_popupLoadingHtml(label));
      popup.document.close();
    } catch (e) {
      // about:blank should be same-origin; ignore if a browser blocks the write.
    }
  }
  return popup;
};

/**
 * useOAuthPopupConnect
 *
 * Owns the popup handshake for one provider:
 *   - registers the `EXTERNAL_CONNECTION_RESULT` postMessage listener and calls
 *     `onResult({ result, reason })` when the popup reports back, then closes it.
 *   - polls `popup.closed` so a user who closes the popup mid-auth (cancel)
 *     cleanly resets `isConnecting` instead of leaving the button spinning.
 *   - returns `startConnect({ body })` which opens the popup synchronously,
 *     POSTs to `/api/externalConnections/:providerId/connect`, then points the
 *     popup at the provider. Resolves on success; throws on network error (with
 *     the popup closed) so the caller can surface its own error copy.
 *   - returns `isConnecting` so the caller can drive an oolib button `isLoading`.
 *
 * The decision to use the popup vs. a full-page redirect is made synchronously
 * at open time — once a popup exists we never navigate the parent tab, so a user
 * closing the popup mid-handshake can't drop the whole app into the OAuth flow.
 *
 * @param {string}   providerId    e.g. "intercom" — drives the connect URL + name
 * @param {string}   providerName  e.g. "Intercom" — loading-screen copy
 * @param {Function} onResult      ({ result: "success"|"error", reason?: string }) => void
 * @returns {{ startConnect: (opts?: { body?: object }) => Promise<void>, isConnecting: boolean }}
 */
export const useOAuthPopupConnect = ({ providerId, providerName, onResult }) => {
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const [isConnecting, setIsConnecting] = useState(false);
  // Keep the latest onResult without re-subscribing the listener every render.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  // Stop watching the popup and reset the connecting state. Idempotent — safe to
  // call from the result message, the close poll, or an error.
  const reset = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    popupRef.current = null;
    setIsConnecting(false);
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (
        !data ||
        data.type !== "EXTERNAL_CONNECTION_RESULT" ||
        data.provider !== providerId
      ) {
        return;
      }
      onResultRef.current &&
        onResultRef.current({ result: data.result, reason: data.reason });
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
      reset();
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [providerId, reset]);

  // Stop the poll if the component unmounts mid-connect.
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const startConnect = useCallback(
    async ({ body } = {}) => {
      // Open the popup synchronously (inside the click stack) so blockers don't
      // kill it. Whether we got one is decided NOW, before the await.
      const popup = openOAuthPopup(`${providerId}_oauth`, providerName);
      const usingPopup = !!popup;
      setIsConnecting(true);
      try {
        const { data } = await axios.post(
          `/api/externalConnections/${providerId}/connect`,
          {
            returnTo: window.location.pathname + window.location.search,
            ...(body || {}),
          }
        );
        if (!data.redirectUrl) throw new Error("No redirectUrl returned");
        if (usingPopup) {
          // User may have closed the blank popup while /connect was in flight.
          // Abort quietly — do NOT redirect the parent tab.
          if (popup.closed) {
            reset();
            return;
          }
          popupRef.current = popup;
          popup.location.href = data.redirectUrl;
          // Watch for a user-closed popup (cancel): no result message will ever
          // arrive, so poll until it's gone and then clear the connecting state.
          pollRef.current = setInterval(() => {
            if (!popupRef.current || popupRef.current.closed) reset();
          }, 500);
        } else {
          // Popup blocked at open time → fall back to the full-page redirect.
          // The page navigates away; the caller's mount-time
          // ?externalConnectionResult effect handles the return.
          window.location.assign(data.redirectUrl);
        }
      } catch (err) {
        if (popup && !popup.closed) popup.close();
        reset();
        throw err;
      }
    },
    [providerId, providerName, reset]
  );

  return { startConnect, isConnecting };
};
